// @ts-nocheck
import { showDialog } from '@/utils/dialogs';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ActivityIndicator,
  Modal,
  View,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter , useFocusEffect } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { Text } from '@/components/ui/text';
import { WebAppShell } from '@/design-system/components';
import { reviewDesk } from '@/design-system';

import { useCredits } from '../../context/CreditContext';

// Trial Welcome Banner
import TrialWelcomeBanner from '../../components/trial/TrialWelcomeBanner';
import { useTrialContext } from '../../context/TrialContext';
import { WhatToWorkOnNextCard } from '../../components/progress/WhatToWorkOnNextCard';
import { useAccessPolicy } from '../../hooks/useAccessPolicy';
import { useResponsive } from '../../hooks/useResponsive';
import SampleReportViewer from '../../components/onboarding/SampleReportViewer';
import { useTips } from '../../context/TipsContext';

// Services & Types
import { chatAPI, authAPI, documentSharingAPI, userDocumentsAPI } from '../../services/api';
import { ICFProgressAPI, isIcfProgressAccessDeniedError } from '../../services/api/icfProgressAPI';
import { ChatSession, User, IntakeSummary, SharedDocument } from '../../types';
import { ProgressDashboardPayload } from '../../types/icfProgress';
import { isSuperAdmin, isSchoolAdmin, isB2CUser, isB2B2CStudent, isB2B2CMentor, isB2B2CAdmin } from '../../utils/roleUtils';
import { formatServerDate } from '../../utils/serverDate';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES, isAnalysisSession, reportRoute, sessionRoute } from '../../navigation/routes';
import { formatLevel } from '../../utils/icfLevels';
import {
  trackChecklistCompleted,
  trackChecklistItemCompleted,
  trackFeatureUsage,
} from '../../services/analytics';
import { brand } from '../../design-system/theme/tokens';

// Professional Design Tokens (matching Redesigned version)
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
};

interface DashboardStats {
  totalSessions: number;
  recentSessions: ChatSession[];
}

interface DashboardScreenProps {
  onLogout?: () => void;
  user?: User;
}

function buildMessagePreview(content?: string | null) {
  if (!content) {
    return '';
  }

  return content
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/[`#>*_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function looksLikeEnglishCopy(value?: string | null) {
  if (!value) return false;
  return /\b(upload|analyzed|coaching|session|readiness|pattern|client|practice|awareness|target)\b/i.test(value);
}

/**
 * Dashboard (web only, D1): latest feedback, activation checklist, recent
 * sessions (each opening its report or chat), ICF progress teaser.
 */
export default function DashboardScreenEnhanced({ onLogout, user: propUser }: DashboardScreenProps) {
  const router = useRouter();
  const { t, language } = useTranslation();
  const insets = useSafeAreaInsets();
  const { width: windowWidth, isPhone, isCompact } = useResponsive();
  const accessPolicy = useAccessPolicy();
  const { showTip } = useTips();
  const canUseIcfProgress = accessPolicy.hasProgressFeature
    || (accessPolicy.isB2C && accessPolicy.isSubscriptionActive && accessPolicy.planTier === 'tier1');
  const [user, setUser] = useState<User | null>(propUser || null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [intakeSummary, setIntakeSummary] = useState<IntakeSummary | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [recentShared, setRecentShared] = useState<SharedDocument[]>([]);
  const [welcomeSession, setWelcomeSession] = useState<ChatSession | null>(null);
  const [welcomePreview, setWelcomePreview] = useState<string | null>(null);
  const [transcriptCount, setTranscriptCount] = useState(0);
  const [processedTranscriptCount, setProcessedTranscriptCount] = useState(0);
  const [analysisCount, setAnalysisCount] = useState(0);
  const [progressDashboard, setProgressDashboard] = useState<ProgressDashboardPayload | null>(null);
  const [progressAccessDenied, setProgressAccessDenied] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [levelUpChecklistCompleted, setLevelUpChecklistCompleted] = useState<string[]>([]);
  const dashboardLoadInFlightRef = useRef(false);
  const lastDashboardLoadStartedAtRef = useRef(0);

  useEffect(() => {
    if (intakeSummary?.profile_type === 'mentor') {
      showTip('tip_mentor_training');
    }
  }, [intakeSummary?.profile_type, showTip]);

  useEffect(() => {
    if (!user?.id) return;
    AsyncStorage.getItem(`activation_checklist_level_up_v1:${user.id}`)
      .then((value) => {
        if (value) setLevelUpChecklistCompleted(JSON.parse(value));
      })
      .catch(() => undefined);
  }, [user?.id]);

  // Credits integration
  const { creditStatus, isLoading: creditsLoading, refreshStatus } = useCredits();
  const showCreditStatus =
    !!creditStatus && !creditStatus.usage_hidden && (creditStatus.low_credit_warning ?? false);

  // Trial countdown — surfaces day-2/day-3 urgency once a B2C user has
  // started their legacy app trial. Banner colors escalate as expiry nears.
  const { trialStatus } = useTrialContext();

  // Tutorial integration - auto-start for first-time users

  const loadDashboardData = useCallback(async (showSpinner: boolean = true) => {
    const now = Date.now();
    if (dashboardLoadInFlightRef.current) {
      return;
    }
    if (!showSpinner && now - lastDashboardLoadStartedAtRef.current < 15_000) {
      return;
    }

    dashboardLoadInFlightRef.current = true;
    lastDashboardLoadStartedAtRef.current = now;

    if (showSpinner) {
      setLoading(true);
    }

    try {
      let currentUser = user;
      if (!currentUser) {
        const userData = await authAPI.getCurrentUser();
        setUser(userData);
        currentUser = userData;
      }

      const sessions = await chatAPI.getSessions();
      const sortedSessions = [...sessions].sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      const currentWelcomeSession = sortedSessions.find((session) => session.session_type === 'welcome') || null;
      const regularSessions = sortedSessions.filter((session) => session.session_type !== 'welcome');

      setWelcomeSession(currentWelcomeSession);

      setStats({
        totalSessions: regularSessions.length,
        recentSessions: regularSessions.slice(0, 5),
      });

      const [
        intakeResult,
        transcriptResult,
        analysisResult,
        progressResult,
        welcomeMessages,
      ] = await Promise.all([
        authAPI.getIntakeSummary().catch((err) => {
          console.warn('Failed to load intake summary', err);
          return null;
        }),
        userDocumentsAPI.list(0, 50, 'user_transcript').catch((err) => {
          console.warn('Failed to load transcript documents', err);
          return null;
        }),
        userDocumentsAPI.list(0, 50, 'user_analysis').catch((err) => {
          console.warn('Failed to load analysis documents', err);
          return null;
        }),
        canUseIcfProgress && !progressAccessDenied
          ? ICFProgressAPI.getDashboard({ cacheMs: 15000, allowTeaser: true })
              .then((data) => ({ data, denied: false }))
              .catch((err) => {
                const denied = isIcfProgressAccessDeniedError(err);
                if (!denied) {
                  console.warn('Failed to load ICF dashboard summary', err);
                }
                return { data: null, denied };
              })
          : Promise.resolve({ data: null, denied: progressAccessDenied }),
        currentWelcomeSession
          ? chatAPI.getMessages(currentWelcomeSession.id).catch((err) => {
              console.warn('Failed to load welcome session messages', err);
              return [];
            })
          : Promise.resolve([]),
      ]);

      setIntakeSummary(intakeResult);
      setProgressAccessDenied(Boolean(progressResult?.denied));
      setProgressDashboard(canUseIcfProgress && !progressResult?.denied ? progressResult?.data : null);

      const transcriptDocuments = transcriptResult?.documents || [];
      const analysisDocuments = analysisResult?.documents || [];

      setTranscriptCount(transcriptDocuments.length);
      setProcessedTranscriptCount(
        transcriptDocuments.filter((doc) => doc.processed || doc.transcription_status === 'completed').length
      );
      setAnalysisCount(analysisDocuments.length);

      const firstAssistantMessage = welcomeMessages.find((message) => message.role === 'assistant');
      setWelcomePreview(
        firstAssistantMessage
          ? buildMessagePreview(firstAssistantMessage.content)
          : null
      );

      // Load shared documents for B2B users
      if (currentUser && (isB2B2CStudent(currentUser) || isB2B2CMentor(currentUser) || isB2B2CAdmin(currentUser))) {
        try {
          const shared = await documentSharingAPI.getSharedWithMe();
          // Get the 3 most recent
          const recentlyShared = [...shared]
            .sort((a, b) => new Date(b.shared_at).getTime() - new Date(a.shared_at).getTime())
            .slice(0, 3);
          setRecentShared(recentlyShared);
        } catch (err) {
          console.warn('Failed to load shared documents', err);
        }
      } else {
        setRecentShared([]);
      }
    } catch {
      if (showSpinner) {
        showDialog(t('common.error'), t('dashboard.failedToLoad'));
      } else {
        console.warn('Failed to refresh dashboard data');
      }
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
      dashboardLoadInFlightRef.current = false;
    }
  }, [user, canUseIcfProgress, progressAccessDenied, t]);

  useEffect(() => {
    setProgressAccessDenied(false);
  }, [canUseIcfProgress, accessPolicy.planTier, accessPolicy.isSubscriptionActive]);

  // Initial load on mount
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Refresh sessions when dashboard gains focus (e.g., after creating new chats)
  useFocusEffect(
    useCallback(() => {
      trackFeatureUsage('dashboard', 'viewed');
      if (!loading) {
        void loadDashboardData(false);
      }
      // Keep credit status fresh (picks up changes from chat/analysis)
      refreshStatus();
    }, [loadDashboardData, loading, refreshStatus])
  );

  // D6: no tutorial auto-start. First-run education is the pre-paywall tour
  // plus the activation checklist below; the spotlight tour is opt-in from
  // Profile -> "Replay tutorial" (ONB-5/21).

  const formatDate = (dateString: string) => formatServerDate(dateString, language);

  const canUploadDocuments = user && (isSuperAdmin(user) || (isSchoolAdmin(user) && isB2CUser(user)));
  const isB2BUser = user && (isB2B2CStudent(user) || isB2B2CMentor(user) || isB2B2CAdmin(user));
  const goal = intakeSummary?.primary_goal?.trim();
  const credentials = intakeSummary?.credentials?.trim();
  const isActivated = analysisCount > 0 || processedTranscriptCount > 0;
  const activationChecklist = [
    {
      key: 'upload',
      label: t('dashboard.activationItems.upload'),
      complete: transcriptCount > 0,
      route: ROUTES.ACTIVATION_UPLOAD_FIRST_SESSION,
    },
    {
      key: 'analysis',
      label: t('dashboard.activationItems.analysis'),
      complete: isActivated,
      route: ROUTES.TABS_CHAT,
    },
    {
      key: 'reply',
      label: t('dashboard.activationItems.reply'),
      complete: (stats?.totalSessions || 0) > 0,
      route: ROUTES.TABS_CHAT,
    },
  ];
  const levelUpChecklist = [
    { key: 'pdf_export', label: t('dashboard.activationItems.pdfExport'), route: ROUTES.TABS_CHAT },
    { key: 'icf_progress', label: t('dashboard.activationItems.icfProgress'), route: ROUTES.TABS_PROGRESS },
    { key: 'development_plan', label: t('dashboard.activationItems.developmentPlan'), route: ROUTES.TABS_PROGRESS },
    { key: 'profile', label: t('dashboard.activationItems.profile'), route: ROUTES.PROFILE },
    { key: 'practice_exam', label: t('dashboard.activationItems.practiceExam'), route: ROUTES.TABS_PROGRESS },
    // "Add a document to your library" goes to the user's Library, not the
    // admin-only RAG Documents screen (CHAT-26 / VIS-CORE-14).
    { key: 'documents', label: t('dashboard.activationItems.documents'), route: ROUTES.TABS_LIBRARY },
  ].map((item) => ({ ...item, complete: levelUpChecklistCompleted.includes(item.key) }));
  const visibleChecklist = isActivated ? levelUpChecklist : activationChecklist;
  const completedChecklistCount = visibleChecklist.filter((item) => item.complete).length;
  const checklistProgress = visibleChecklist.length > 0
    ? completedChecklistCount / visibleChecklist.length
    : 0;

  const handleChecklistItemPress = async (item: typeof levelUpChecklist[number]) => {
    if (isActivated && !item.complete && user?.id) {
      const nextCompleted = [...new Set([...levelUpChecklistCompleted, item.key])];
      setLevelUpChecklistCompleted(nextCompleted);
      trackChecklistItemCompleted(item.key, 'level_up');
      await AsyncStorage.setItem(
        `activation_checklist_level_up_v1:${user.id}`,
        JSON.stringify(nextCompleted),
      );
      if (nextCompleted.length === levelUpChecklist.length) {
        const completionKey = `activation_checklist_level_up_completed_v1:${user.id}`;
        const alreadyCompleted = await AsyncStorage.getItem(completionKey);
        if (!alreadyCompleted) {
          trackChecklistCompleted();
          await AsyncStorage.setItem(completionKey, '1');
        }
      }
    }
    router.push(item.route as any);
  };
  const personalizedEmptyTitle = goal
    ? t('dashboard.activationGoalTitle', { goal })
    : t('dashboard.noConversationsYet');
  let personalizedEmptyDescription = t('dashboard.startChatWithAI');

  if (goal && credentials) {
    personalizedEmptyDescription = t(
      'dashboard.activationGoalWithCredentialsBody',
      { credentials, goal }
    );
  } else if (goal) {
    personalizedEmptyDescription = t(
      'dashboard.activationGoalBody',
      { goal }
    );
  } else if (credentials) {
    personalizedEmptyDescription = t(
      'dashboard.activationCredentialsBody',
      { credentials }
    );
  }

  if (loading) {
    return (
      <SafeAreaView className="flex-1 bg-surface">
        <View className="flex-1 items-center justify-center p-8">
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text className="mt-4 text-base text-muted-foreground">
            {t('dashboard.loadingData')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // D1: web-only; the native render tree was deleted.
  const isCompactWeb = isCompact;
  const isMidWeb = !isPhone && isCompact;
  const isNarrowWeb = isPhone;
  const showIcfProgressContent = canUseIcfProgress && !progressAccessDenied;
  const firstName = user?.full_name?.split(' ')?.[0] || t('profile.notSet');
  // CORE-1: real date and kind for every row (no hard-coded "2 days ago · Completed").
  const sessionKindLabel = (session: ChatSession) => (
    isAnalysisSession(session) ? t('report.kindReport') : t('report.kindChat')
  );
  const recentItems = (stats?.recentSessions || []).slice(0, 4).map((session, index) => ({
    id: session.id,
    title: session.title || `${t('dashboard.session')} ${session.id}`,
    subtitle: `${formatDate(session.created_at)} · ${sessionKindLabel(session)}`,
    active: index === 0,
    onPress: () => router.push(sessionRoute(session) as never),
  }));

  const latestSession = stats?.recentSessions?.[0];
  const suggestedFollowupQuestion = showIcfProgressContent
    ? t('dashboard.webPccQuestion')
    : t('dashboard.webGeneralFollowupQuestion');
  const startUpload = () => {
    trackFeatureUsage('audio_upload', 'initiated', { source: 'web_dashboard_primary' });
    router.push(ROUTES.TABS_LIBRARY);
  };
  const openSuggestedFollowup = () => {
    trackFeatureUsage('dashboard_followup_question', 'opened', {
      source: 'web_dashboard_latest_feedback',
      has_session: Boolean(latestSession),
    });

    router.push({
      pathname: ROUTES.TABS_CHAT,
      params: {
        ...(latestSession ? { sessionId: String(latestSession.id) } : {}),
        suggestedQuestion: suggestedFollowupQuestion,
      },
    });
  };
  const openHowItWorks = () => {
    trackFeatureUsage('dashboard_how_it_works', 'opened', { source: 'web_dashboard_upload_card' });
    setShowHowItWorks(true);
  };
  const translateDataText = (value: string | undefined | null, fallback: string) => {
    const trimmed = value?.trim();
    if (!trimmed || (language === 'tr' && looksLikeEnglishCopy(trimmed))) {
      return fallback;
    }
    return trimmed;
  };
  const readiness = progressDashboard?.readiness;
  const latestCompletedAnalysis = progressDashboard?.recent_analyses?.find(
    (analysis) => analysis.analysis_status === 'completed'
  );
  const trialAnalysisHighlights = (
    latestCompletedAnalysis?.session_insights?.positive_highlights
    || progressDashboard?.insights?.key_strengths
    || progressDashboard?.strengths
    || []
  ).filter(Boolean).slice(0, 3);
  const trialAnalysisSummary = progressDashboard?.insights?.overall_assessment?.trim()
    || readiness?.summary?.trim()
    || null;
  const trialAnalysisSessionId = latestCompletedAnalysis?.session_id ?? latestSession?.id ?? null;
  const trialAnalysisTitle = latestCompletedAnalysis?.session_title?.trim()
    || latestSession?.title
    || null;
  const showTrialAnalysisPreview = Boolean(
    trialStatus?.active
    && trialAnalysisSessionId
    && trialAnalysisTitle
    && (latestCompletedAnalysis || analysisCount > 0 || processedTranscriptCount > 0)
  );
  const analyzedSessionCount = progressDashboard?.progress?.total_sessions_analyzed ?? analysisCount ?? 0;
  const readinessStrength = readiness?.recurring_patterns?.find((pattern) => pattern.pattern_type === 'strength');
  const readinessDevelopment = readiness?.recurring_patterns?.find((pattern) => pattern.pattern_type === 'development');
  const nextPractice = readiness?.practice_plan?.[0];
  // Translate the level itself; the backend label is canonical ("Ready for PCC").
  const estimatedLevel = formatLevel(
    t,
    readiness?.estimated_level_key ?? readiness?.estimated_level,
    analyzedSessionCount > 0 ? 'icfProgress.noData' : 'dashboard.webNoProgressYet'
  );
  const strongArea = translateDataText(
    readinessStrength?.title || progressDashboard?.strengths?.[0],
    t('dashboard.webNoProgressYet')
  );
  const focusArea = translateDataText(
    nextPractice?.title || readinessDevelopment?.title || progressDashboard?.development_areas?.[0],
    t('dashboard.webNoProgressYet')
  );
  const weeklyFocusTitle = translateDataText(
    nextPractice?.title || readinessDevelopment?.title,
    t('dashboard.webWeeklyFocusFallback')
  );
  const weeklyFocusCopy = translateDataText(
    nextPractice?.try_in_next_session?.[0] || nextPractice?.rationale || readinessDevelopment?.description,
    t('dashboard.webFocusCopy')
  );
  const progressGateTitle = accessPolicy.isB2BUser
    ? t('dashboard.webProgressLockedSchoolTitle')
    : t('dashboard.webProgressLockedTitle');
  const progressGateCopy = accessPolicy.isB2BUser
    ? t(
        'dashboard.webProgressLockedSchoolCopy'
      )
    : t(
        'dashboard.webProgressLockedCopy'
      );
  const progressGateCta = accessPolicy.canManageSchoolBilling
    ? t('dashboard.webProgressLockedSchoolCta')
    : t('dashboard.webProgressLockedCta');
  const showProgressGateCta = accessPolicy.isB2C || accessPolicy.canManageSchoolBilling;
  const openProgressGate = () => {
    if (accessPolicy.canManageSchoolBilling) {
      router.push(ROUTES.TABS_SCHOOL_MANAGEMENT);
      return;
    }
    router.push(ROUTES.PROFILE);
  };

  return (
    <WebAppShell
      active="dashboard"
      recentItems={recentItems}
      topRight={(
        <>
          <TouchableOpacity
            style={webStyles.userChip}
            onPress={() => router.push(ROUTES.PROFILE)}
            accessibilityRole="button"
          >
            <View style={webStyles.avatarCircle}>
              <Text style={webStyles.avatarText}>
                {(user?.full_name || user?.email || 'BK').slice(0, 2).toUpperCase()}
              </Text>
            </View>
            <Text style={webStyles.userChipText} numberOfLines={1}>
              {user?.full_name || user?.email || t('profile.notSet')}
            </Text>
            <MaterialIcons name="keyboard-arrow-down" size={18} color={reviewDesk.inkSoft} />
          </TouchableOpacity>
        </>
      )}
    >
      {trialStatus?.active ? (
        <View style={{ marginBottom: 16 }}>
          <TrialWelcomeBanner intakeSummary={intakeSummary} onIntakeUpdated={setIntakeSummary} />
        </View>
      ) : null}
      <View style={[webStyles.heroRow, isCompactWeb && webStyles.heroRowCompact]}>
        <View>
          <Text style={webStyles.webTitle}>
            {t('dashboard.webGreeting', { name: firstName })}
          </Text>
          <Text style={webStyles.webSubtitle}>
            {t(
              'dashboard.webGreetingSubtitle'
            )}
          </Text>
        </View>
        {latestSession ? (
          <View style={webStyles.statusPill}>
            <MaterialIcons name="schedule" size={16} color={reviewDesk.accent} />
            <Text style={webStyles.statusPillText}>
              {t('dashboard.webLatestAnalysisAvailable')}
            </Text>
          </View>
        ) : null}
      </View>

      <View
        style={[webStyles.uploadCard, isCompactWeb && webStyles.uploadCardCompact]}
        nativeID="dashboard_upload_card"
        testID="dashboard_upload_card"
      >
        <View style={[webStyles.uploadIconWrap, isCompactWeb && webStyles.uploadIconWrapCompact]}>
          <MaterialIcons name="cloud-upload" size={38} color={reviewDesk.accent} />
        </View>
        <View style={webStyles.uploadTextBlock}>
          <Text style={webStyles.cardTitle}>{t('dashboard.webUploadTitle')}</Text>
          <Text style={webStyles.cardCopy}>
            {t(
              'dashboard.webUploadBody'
            )}
          </Text>
          <View style={[webStyles.uploadActions, isCompactWeb && webStyles.uploadActionsCompact]}>
            <TouchableOpacity style={webStyles.primaryButton} onPress={startUpload} accessibilityRole="button">
              <MaterialIcons name="upload-file" size={18} color="#FFFFFF" />
              <Text className="text-white" style={webStyles.primaryButtonText}>
                {t('dashboard.webUploadCta')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={webStyles.textLinkButton}
              onPress={openHowItWorks}
              accessibilityRole="button"
            >
              <Text style={webStyles.textLink}>{t('dashboard.webHowItWorks')}</Text>
              <MaterialIcons name="arrow-forward" size={17} color={reviewDesk.accent} />
            </TouchableOpacity>
          </View>
        </View>
        <View style={[webStyles.trustChips, isCompactWeb && webStyles.trustChipsCompact]}>
          {[
            ['lock-outline', t('dashboard.webSecure')],
            ['verified', t('dashboard.webIcfAligned')],
            ['language', t('dashboard.webTurkishSupported')],
          ].map(([icon, label]) => (
            <View key={label} style={webStyles.trustChip}>
              <MaterialIcons name={icon as any} size={16} color={reviewDesk.inkSoft} />
              <Text style={webStyles.trustChipText}>{label}</Text>
            </View>
          ))}
        </View>
      </View>

      {progressDashboard?.development_plan ? (
        <View style={{ marginBottom: 18 }}>
          <WhatToWorkOnNextCard
            plan={progressDashboard.development_plan}
            locked={'locked' in progressDashboard && progressDashboard.locked === true}
            testID="dashboard_development_plan_card"
            surface="dashboard"
          />
        </View>
      ) : null}

      <View style={[webStyles.twoColumnGrid, isCompactWeb && webStyles.stackGrid]}>
        <View style={[webStyles.panelCard, isCompactWeb && webStyles.panelCardCompact]}>
          <Text style={webStyles.panelTitle}>{t('dashboard.webLatestFeedback')}</Text>
          {showTrialAnalysisPreview && trialAnalysisTitle ? (
            <View style={webStyles.analysisPreview}>
              <View style={webStyles.analysisPreviewHeader}>
                <View style={webStyles.analysisPreviewIcon}>
                  <MaterialIcons name="auto-awesome" size={22} color={reviewDesk.accent} />
                </View>
                <View style={webStyles.analysisPreviewTitleBlock}>
                  <Text style={webStyles.analysisPreviewEyebrow}>
                    {t('dashboard.webTrialAnalysisPreview')}
                  </Text>
                  <Text style={webStyles.feedbackTitle} numberOfLines={2}>
                    {trialAnalysisTitle}
                  </Text>
                </View>
                <View style={webStyles.analysisPreviewBadges}>
                  {typeof latestCompletedAnalysis?.coaching_quality_score === 'number' ? (
                    <View style={webStyles.analysisScoreBadge}>
                      <Text style={webStyles.analysisScoreText}>
                        {t('dashboard.webAnalysisScore', {
                          score: Math.round(latestCompletedAnalysis.coaching_quality_score),
                        })}
                      </Text>
                    </View>
                  ) : null}
                  {readiness?.estimated_level ? (
                    <View style={webStyles.analysisLevelBadge}>
                      <Text style={webStyles.analysisLevelText}>
                        {t('dashboard.webAnalysisLevel', { level: formatLevel(t, readiness.estimated_level) })}
                      </Text>
                    </View>
                  ) : (
                    <View style={webStyles.analysisLevelBadge}>
                      <MaterialIcons name="check-circle-outline" size={14} color={reviewDesk.accent} />
                      <Text style={webStyles.analysisLevelText}>{t('dashboard.webCompleted')}</Text>
                    </View>
                  )}
                </View>
              </View>
              {trialAnalysisSummary ? (
                <Text style={webStyles.analysisSummary} numberOfLines={3}>
                  {trialAnalysisSummary}
                </Text>
              ) : (
                <Text style={webStyles.analysisSummary} numberOfLines={3}>
                  {t('dashboard.webAnalysisReadyCopy')}
                </Text>
              )}
              {trialAnalysisHighlights.length > 0 ? (
                <View style={webStyles.analysisHighlights}>
                  <Text style={webStyles.analysisHighlightsLabel}>
                    {t('dashboard.webAnalysisHighlights')}
                  </Text>
                  {trialAnalysisHighlights.map((highlight, index) => (
                    <View key={`${highlight}-${index}`} style={webStyles.analysisHighlightRow}>
                      <View style={webStyles.analysisHighlightDot} />
                      <Text style={webStyles.analysisHighlightText} numberOfLines={2}>
                        {highlight}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          ) : latestSession ? (
            <View style={webStyles.feedbackRow}>
              <View style={webStyles.fileIconCircle}>
                <MaterialIcons name="description" size={25} color={reviewDesk.inkSoft} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={webStyles.feedbackTitle}>{latestSession.title}</Text>
                <View style={webStyles.successInline}>
                  <MaterialIcons name="schedule" size={15} color={reviewDesk.accent} />
                  <Text style={webStyles.successInlineText}>
                    {`${formatDate(latestSession.created_at)} · ${sessionKindLabel(latestSession)}`}
                  </Text>
                </View>
              </View>
            </View>
          ) : (
            <View style={webStyles.feedbackRow}>
              <View style={webStyles.fileIconCircle}>
                <MaterialIcons name="add-chart" size={25} color={reviewDesk.inkSoft} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={webStyles.feedbackTitle}>{t('dashboard.webNoAnalysisTitle')}</Text>
                <Text style={webStyles.cardCopy}>{t('dashboard.webNoAnalysisCopy')}</Text>
              </View>
            </View>
          )}
          <TouchableOpacity
            style={webStyles.secondaryButton}
            onPress={() => {
              if (showTrialAnalysisPreview && trialAnalysisSessionId) {
                router.push(reportRoute(trialAnalysisSessionId) as never);
              } else if (latestSession) {
                router.push(sessionRoute(latestSession) as never);
              } else {
                router.push(ROUTES.TABS_CHAT);
              }
            }}
          >
            <Text style={webStyles.secondaryButtonText}>{t('dashboard.webOpenFeedback')}</Text>
            <MaterialIcons name="arrow-forward" size={18} color={reviewDesk.accent} />
          </TouchableOpacity>
          <TouchableOpacity
            style={webStyles.followupChip}
            onPress={openSuggestedFollowup}
            activeOpacity={0.84}
            accessibilityRole="button"
            accessibilityLabel={`${t('dashboard.webSuggestedFollowup')}: ${suggestedFollowupQuestion}`}
          >
            <View style={webStyles.followupCopyBlock}>
              <Text style={webStyles.followupLabel}>{t('dashboard.webSuggestedFollowup')}</Text>
              <Text style={webStyles.followupText} numberOfLines={2}>
                {suggestedFollowupQuestion}
              </Text>
            </View>
            <View style={webStyles.followupIconWrap}>
              <MaterialIcons name="arrow-forward" size={16} color={reviewDesk.amber} />
            </View>
          </TouchableOpacity>
        </View>

        {showIcfProgressContent ? (
          <View style={[webStyles.panelCard, isCompactWeb && webStyles.panelCardCompact]}>
            <Text style={webStyles.panelTitle}>{t('dashboard.webWeeklyFocus')}</Text>
            <View style={webStyles.focusRow}>
              <View style={webStyles.focusIcon}>
                <MaterialIcons name="track-changes" size={26} color={reviewDesk.amber} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={webStyles.feedbackTitle}>{weeklyFocusTitle}</Text>
                <Text style={webStyles.focusCopy}>
                  {weeklyFocusCopy}
                </Text>
              </View>
            </View>
            <TouchableOpacity style={webStyles.secondaryButton} onPress={() => router.push(ROUTES.TABS_PROGRESS)}>
              <Text style={webStyles.secondaryButtonText}>{t('dashboard.webOpenExercise')}</Text>
              <MaterialIcons name="arrow-forward" size={18} color={reviewDesk.accent} />
            </TouchableOpacity>
          </View>
        ) : (
          <View style={[webStyles.panelCard, webStyles.lockedProgressCard, isCompactWeb && webStyles.panelCardCompact]}>
            <View style={webStyles.lockedProgressTop}>
              <View style={webStyles.lockedProgressIcon}>
                <MaterialIcons name="lock-outline" size={24} color={reviewDesk.accent} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={webStyles.feedbackTitle}>{progressGateTitle}</Text>
                <Text style={webStyles.focusCopy}>{progressGateCopy}</Text>
              </View>
            </View>
            {showProgressGateCta ? (
              <TouchableOpacity style={webStyles.secondaryButton} onPress={openProgressGate} accessibilityRole="button">
                <Text style={webStyles.secondaryButtonText}>{progressGateCta}</Text>
                <MaterialIcons name="arrow-forward" size={18} color={reviewDesk.accent} />
              </TouchableOpacity>
            ) : null}
          </View>
        )}
      </View>

      {showIcfProgressContent ? (
        <View
          style={[
            webStyles.snapshotGrid,
            isMidWeb && webStyles.snapshotGridMid,
            isNarrowWeb && webStyles.stackGrid,
          ]}
        >
          {[
            ['bar-chart', `${analyzedSessionCount}`, t('dashboard.webSessionsAnalyzed')],
            ['star-border', estimatedLevel, t('dashboard.webEstimatedLevel')],
            ['person-outline', strongArea, t('dashboard.webStrongArea')],
            ['lightbulb-outline', focusArea, t('dashboard.webFocus')],
          ].map(([icon, value, label]) => (
            <View
              key={`${value}-${label}`}
              style={[webStyles.statTile, isMidWeb && webStyles.statTileMid]}
            >
              <MaterialIcons name={icon as any} size={25} color={reviewDesk.accent} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={webStyles.statValue} numberOfLines={2}>{value}</Text>
                <Text style={webStyles.statLabel} numberOfLines={2}>{label}</Text>
              </View>
            </View>
          ))}
        </View>
      ) : null}

      <View style={webStyles.recentPanel}>
        <Text style={webStyles.panelTitle}>{t('dashboard.webRecentFeedback')}</Text>
        {stats?.recentSessions?.length ? stats.recentSessions.slice(0, 3).map((session, index) => (
          <TouchableOpacity
            key={session.id}
            style={[
              webStyles.recentFeedbackRow,
              index === 0 && webStyles.recentFeedbackRowFirst,
              isCompactWeb && webStyles.recentFeedbackRowCompact,
            ]}
            testID={`dashboard_recent_session_${session.id}`}
            onPress={() => {
              if (typeof session.id === 'number') {
                router.push(sessionRoute(session) as never);
              }
            }}
          >
            <MaterialIcons name="description" size={18} color={reviewDesk.inkSoft} />
            <Text style={webStyles.recentFeedbackTitle} numberOfLines={1}>{session.title}</Text>
            <Text style={webStyles.recentFeedbackDate}>{formatDate(session.created_at)}</Text>
            <View style={[webStyles.rowStatus, !isAnalysisSession(session) && webStyles.rowStatusDraft]}>
              <Text style={[webStyles.rowStatusText, !isAnalysisSession(session) && webStyles.rowStatusDraftText]}>
                {sessionKindLabel(session)}
              </Text>
            </View>
            <MaterialIcons name="chevron-right" size={18} color={reviewDesk.muted} />
          </TouchableOpacity>
        )) : (
          <TouchableOpacity
            style={webStyles.feedbackRow}
            onPress={() => setShowSampleReport(true)}
            accessibilityRole="button"
            accessibilityLabel={t('dashboard.sampleSessionOpen')}
          >
            <MaterialIcons name="verified" size={22} color={reviewDesk.accent} />
            <View style={{ flex: 1, gap: 3 }}>
              <Text style={webStyles.recentFeedbackTitle}>{t('dashboard.sampleSessionTitle')}</Text>
              <Text style={webStyles.cardCopy}>{t('dashboard.sampleSessionMeta')}</Text>
            </View>
            <View style={webStyles.rowStatus}>
              <Text style={webStyles.rowStatusText}>{t('dashboard.sampleBadge')}</Text>
            </View>
            <MaterialIcons name="chevron-right" size={18} color={reviewDesk.muted} />
          </TouchableOpacity>
        )}
      </View>

      <Modal
        visible={showHowItWorks}
        transparent
        animationType="fade"
        onRequestClose={() => setShowHowItWorks(false)}
      >
        <View style={webStyles.modalBackdrop}>
          <View style={webStyles.howModal}>
            <View style={webStyles.modalHeader}>
              <Text style={webStyles.modalTitle}>{t('dashboard.webHowModalTitle')}</Text>
              <TouchableOpacity
                style={webStyles.modalCloseButton}
                onPress={() => setShowHowItWorks(false)}
                accessibilityRole="button"
              >
                <MaterialIcons name="close" size={20} color={reviewDesk.inkSoft} />
              </TouchableOpacity>
            </View>
            <Text style={webStyles.modalCopy}>
              {t('dashboard.webHowModalBody')}
            </Text>
            {[
              ['upload-file', t('dashboard.webHowStepOne')],
              ['auto-awesome', t('dashboard.webHowStepTwo')],
              ['chat-bubble-outline', t('dashboard.webHowStepThree')],
            ].map(([icon, label]) => (
              <View key={label} style={webStyles.modalStep}>
                <View style={webStyles.modalStepIcon}>
                  <MaterialIcons name={icon as any} size={19} color={reviewDesk.accent} />
                </View>
                <Text style={webStyles.modalStepText}>{label}</Text>
              </View>
            ))}
            <TouchableOpacity
              style={webStyles.modalPrimaryButton}
              onPress={() => setShowHowItWorks(false)}
              accessibilityRole="button"
            >
              <Text className="text-white" style={webStyles.modalPrimaryText}>{t('common.ok')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
      <SampleReportViewer
        visible={showSampleReport}
        surface="dashboard_empty_state"
        primaryAction="upload"
        onClose={() => setShowSampleReport(false)}
        onPrimaryPress={() => {
          setShowSampleReport(false);
          router.push(ROUTES.ACTIVATION_UPLOAD_FIRST_SESSION);
        }}
      />
    </WebAppShell>
  );
}

const webStyles = StyleSheet.create({
  iconButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
  },
  userChip: {
    maxWidth: 260,
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  avatarCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accentSoft,
  },
  avatarText: {
    color: reviewDesk.accent,
    fontSize: 12,
    fontWeight: '800',
  },
  userChipText: {
    color: reviewDesk.ink,
    fontSize: 13,
    fontWeight: '600',
  },
  heroRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 18,
  },
  heroRowCompact: {
    flexDirection: 'column',
    gap: 14,
  },
  webTitle: {
    color: reviewDesk.ink,
    fontSize: 32,
    lineHeight: 38,
    fontWeight: '800',
    letterSpacing: -0.4,
  },
  webSubtitle: {
    color: reviewDesk.inkSoft,
    fontSize: 15,
    lineHeight: 22,
    marginTop: 8,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  statusPillText: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    fontWeight: '600',
  },
  uploadCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 26,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 20,
    marginBottom: 18,
  },
  uploadCardCompact: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
    padding: 18,
  },
  uploadIconWrap: {
    width: '100%',
    maxWidth: 104,
    height: 104,
    borderRadius: 52,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.accentWash,
  },
  uploadIconWrapCompact: {
    width: 72,
    height: 72,
    borderRadius: 36,
  },
  uploadTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  cardTitle: {
    color: reviewDesk.ink,
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: -0.15,
  },
  cardCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 15,
    lineHeight: 22,
    marginTop: 8,
    maxWidth: 440,
  },
  uploadActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 18,
    marginTop: 18,
  },
  uploadActionsCompact: {
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 12,
  },
  primaryButton: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    borderRadius: 9,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 22,
    flexShrink: 0,
    boxShadow: '0 10px 22px rgba(30, 64, 175, 0.18)',
  } as any,
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
  textLinkButton: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 14,
    flexShrink: 0,
  },
  textLink: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '700',
  },
  trustChips: {
    width: '100%',
    maxWidth: 166,
    gap: 8,
  },
  trustChipsCompact: {
    width: '100%',
    maxWidth: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  trustChip: {
    minHeight: 38,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: '#FFFDF9',
    paddingHorizontal: 12,
  },
  trustChipText: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    fontWeight: '600',
  },
  twoColumnGrid: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 18,
    marginBottom: 18,
  },
  stackGrid: {
    flexDirection: 'column',
  },
  panelCard: {
    flex: 1,
    minWidth: 0,
    alignSelf: 'stretch',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 20,
  },
  panelCardCompact: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
  },
  panelTitle: {
    color: reviewDesk.ink,
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 18,
  },
  feedbackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 18,
  },
  analysisPreview: {
    borderRadius: 11,
    borderWidth: 1,
    borderColor: 'rgba(30, 64, 175, 0.16)',
    backgroundColor: '#F8FAFF',
    padding: 14,
    gap: 12,
    marginBottom: 18,
  },
  analysisPreviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  analysisPreviewIcon: {
    width: 42,
    height: 42,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(30, 64, 175, 0.14)',
    backgroundColor: reviewDesk.accentSoft,
    flexShrink: 0,
  },
  analysisPreviewTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  analysisPreviewEyebrow: {
    color: reviewDesk.accent,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  analysisPreviewBadges: {
    alignItems: 'flex-end',
    gap: 6,
    flexShrink: 0,
  },
  analysisScoreBadge: {
    borderRadius: 999,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 9,
    paddingVertical: 5,
  },
  analysisScoreText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  analysisLevelBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 999,
    backgroundColor: '#E9F7EF',
    paddingHorizontal: 9,
    paddingVertical: 5,
  },
  analysisLevelText: {
    color: reviewDesk.accent,
    fontSize: 11,
    fontWeight: '800',
  },
  analysisSummary: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 19,
  },
  analysisHighlights: {
    gap: 7,
    borderTopWidth: 1,
    borderTopColor: 'rgba(30, 64, 175, 0.10)',
    paddingTop: 10,
  },
  analysisHighlightsLabel: {
    color: reviewDesk.ink,
    fontSize: 12,
    fontWeight: '800',
  },
  analysisHighlightRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  analysisHighlightDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: reviewDesk.accent,
    marginTop: 6,
    flexShrink: 0,
  },
  analysisHighlightText: {
    flex: 1,
    minWidth: 0,
    color: reviewDesk.inkSoft,
    fontSize: 12,
    lineHeight: 17,
  },
  fileIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.cardMuted,
  },
  feedbackTitle: {
    color: reviewDesk.ink,
    fontSize: 16,
    fontWeight: '800',
  },
  successInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 6,
  },
  successInlineText: {
    color: reviewDesk.accent,
    fontSize: 13,
    fontWeight: '700',
  },
  secondaryButton: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    marginTop: 'auto',
  },
  secondaryButtonText: {
    color: reviewDesk.accent,
    fontSize: 15,
    fontWeight: '800',
  },
  followupChip: {
    marginTop: 12,
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    minHeight: 52,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.18)',
    backgroundColor: reviewDesk.amberSoft,
    paddingHorizontal: 12,
    paddingVertical: 10,
    overflow: 'hidden',
  },
  followupCopyBlock: {
    flex: 1,
    minWidth: 0,
  },
  followupLabel: {
    color: reviewDesk.inkSoft,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 2,
  },
  followupText: {
    color: '#8A5B09',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '800',
    flexShrink: 1,
    minWidth: 0,
  },
  followupIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
  },
  focusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 18,
  },
  focusIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.24)',
    backgroundColor: '#FFF8E9',
  },
  focusCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 20,
    marginTop: 6,
  },
  lockedProgressCard: {
    borderColor: 'rgba(30, 64, 175, 0.16)',
    backgroundColor: '#FFFDF8',
  },
  lockedProgressTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 18,
  },
  lockedProgressIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.accentSoft,
    flexShrink: 0,
  },
  snapshotGrid: {
    flexDirection: 'row',
    gap: 14,
    marginBottom: 18,
  },
  snapshotGridMid: {
    flexWrap: 'wrap',
  },
  statTile: {
    flex: 1,
    minWidth: 0,
    minHeight: 86,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 16,
  },
  statTileMid: {
    flexBasis: '47%',
    flexGrow: 1,
    minWidth: 140,
  },
  statValue: {
    color: reviewDesk.ink,
    fontSize: 18,
    fontWeight: '800',
  },
  statLabel: {
    color: reviewDesk.inkSoft,
    fontSize: 12,
    lineHeight: 16,
    marginTop: 2,
  },
  recentPanel: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 18,
  },
  recentFeedbackRow: {
    minHeight: 52,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
  },
  recentFeedbackRowFirst: {
    borderTopWidth: 0,
  },
  recentFeedbackRowCompact: {
    flexWrap: 'wrap',
    paddingVertical: 12,
  },
  recentFeedbackTitle: {
    flex: 1,
    color: reviewDesk.ink,
    fontSize: 14,
    fontWeight: '700',
  },
  recentFeedbackDate: {
    width: '100%',
    maxWidth: 112,
    color: reviewDesk.muted,
    fontSize: 13,
  },
  rowStatus: {
    minWidth: 92,
    alignItems: 'center',
    borderRadius: 7,
    backgroundColor: reviewDesk.accentSoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  rowStatusText: {
    color: reviewDesk.accent,
    fontSize: 12,
    fontWeight: '800',
  },
  // Chat-session rows (not a report): amber badge.
  rowStatusDraft: {
    backgroundColor: reviewDesk.amberSoft,
  },
  rowStatusDraftText: {
    color: '#8A5B09',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(17, 24, 39, 0.28)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  howModal: {
    width: '100%',
    maxWidth: 440,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 20,
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)',
  } as any,
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  modalTitle: {
    flex: 1,
    color: reviewDesk.ink,
    fontSize: 20,
    fontWeight: '800',
    lineHeight: 25,
  },
  modalCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 21,
    marginBottom: 16,
  },
  modalStep: {
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
  },
  modalStepIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalStepText: {
    color: reviewDesk.ink,
    fontSize: 15,
    fontWeight: '700',
  },
  modalPrimaryButton: {
    minHeight: 44,
    borderRadius: 9,
    backgroundColor: reviewDesk.accent,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  modalPrimaryText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
});
